import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../../App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  FaCrown, FaRocket, FaBuilding, FaHandshake, FaCalendar,
  FaBox, FaKey, FaClipboardList, FaStore, FaArrowUp
} from 'react-icons/fa';

export default function MySubscription() {
  const { API, user } = useContext(AppContext);
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchSubscription();
    }
  }, [user]);

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/user/${user.id}`);
      setSubscription(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPlanIcon = (planId) => {
    const icons = {
      'decouverte': <FaRocket className="text-3xl text-blue-500" />,
      'pro': <FaCrown className="text-3xl text-yellow-500" />,
      'fournisseur': <FaBuilding className="text-3xl text-[#635BFF]" />,
      'sur_mesure': <FaHandshake className="text-3xl text-purple-500" />
    };
    return icons[planId] || <FaRocket className="text-3xl text-gray-500" />;
  };

  const getStatusBadge = (status) => {
    const badges = {
      'trial': { text: 'Essai Gratuit', class: 'bg-blue-500' },
      'active': { text: 'Actif', class: 'bg-[#635BFF]' },
      'expired': { text: 'Expiré', class: 'bg-red-500' },
      'cancelled': { text: 'Annulé', class: 'bg-gray-500' }
    };
    const badge = badges[status] || { text: status, class: 'bg-gray-500' };
    return <Badge className={badge.class}>{badge.text}</Badge>;
  };

  const getPlanName = (planId) => {
    const names = {
      'decouverte': 'Découverte',
      'pro': 'Pro',
      'fournisseur': 'Fournisseur',
      'sur_mesure': 'Sur Mesure'
    };
    return names[planId] || planId;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDaysRemaining = () => {
    if (!subscription) return 0;
    const endDate = subscription.status === 'trial' 
      ? subscription.trial_end 
      : subscription.subscription_end;
    
    if (!endDate) return 0;
    
    const end = new Date(endDate);
    const now = new Date();
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  const getAdUsage = () => {
    if (!subscription) return { used: 0, max: 1 };
    
    const adsCount = subscription.ads_count || { product: 0, rental: 0, request: 0, directory: 0 };
    const plan = subscription.plan;
    
    if (plan === 'decouverte') {
      const used = adsCount.product + adsCount.rental + adsCount.request + adsCount.directory;
      return { used, max: 1 };
    } else if (plan === 'fournisseur') {
      const used = adsCount.product + adsCount.rental + adsCount.directory;
      return { used, max: 20 };
    } else if (plan === 'pro') {
      // Pro has 1 of each
      return null; // Display differently
    }
    return { used: 0, max: 999 };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="text-xl text-gray-600">Chargement...</div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] p-4">
        <div className="container mx-auto max-w-2xl">
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-gray-600 mb-4">Aucun abonnement trouvé.</p>
              <Button onClick={() => navigate('/pricing')}>
                Voir les offres
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const daysRemaining = getDaysRemaining();
  const adUsage = getAdUsage();

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <h1 className="text-3xl font-bold text-[#0A2540] mb-8">Mon Abonnement</h1>

        {/* Current Plan Card */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                {getPlanIcon(subscription.plan)}
                <div>
                  <h2 className="text-2xl font-bold text-[#0A2540]">
                    Pack {getPlanName(subscription.plan)}
                  </h2>
                  <div className="flex items-center gap-2 mt-1">
                    {getStatusBadge(subscription.status)}
                    {subscription.status === 'trial' && (
                      <span className="text-sm text-gray-500">
                        (Expire dans {daysRemaining} jours)
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              {(subscription.status === 'expired' || subscription.status === 'trial') && (
                <Button 
                  className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600"
                  onClick={() => navigate('/pricing')}
                >
                  <FaArrowUp className="mr-2" />
                  {subscription.status === 'expired' ? 'Renouveler' : 'Passer au payant'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Subscription Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Dates */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FaCalendar className="text-[#635BFF]" />
                Période
              </CardTitle>
            </CardHeader>
            <CardContent>
              {subscription.status === 'trial' ? (
                <>
                  <p className="text-sm text-gray-600">
                    <strong>Début essai :</strong> {formatDate(subscription.trial_start)}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Fin essai :</strong> {formatDate(subscription.trial_end)}
                  </p>
                  <div className="mt-4">
                    <p className="text-sm text-gray-500 mb-2">{daysRemaining} jours restants</p>
                    <Progress value={((7 - daysRemaining) / 7) * 100} className="h-2" />
                  </div>
                </>
              ) : subscription.status === 'active' ? (
                <>
                  <p className="text-sm text-gray-600">
                    <strong>Début :</strong> {formatDate(subscription.subscription_start)}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Fin :</strong> {formatDate(subscription.subscription_end)}
                  </p>
                  <div className="mt-4">
                    <p className="text-sm text-gray-500 mb-2">{daysRemaining} jours restants</p>
                    <Progress value={((365 - daysRemaining) / 365) * 100} className="h-2" />
                  </div>
                </>
              ) : (
                <p className="text-red-500">Votre abonnement a expiré</p>
              )}
            </CardContent>
          </Card>

          {/* Ad Usage */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FaBox className="text-[#635BFF]" />
                Utilisation des Annonces
              </CardTitle>
            </CardHeader>
            <CardContent>
              {subscription.plan === 'pro' ? (
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Produits</span>
                    <Badge variant={subscription.ads_count?.product >= 1 ? 'destructive' : 'outline'}>
                      {subscription.ads_count?.product || 0}/1
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Locations</span>
                    <Badge variant={subscription.ads_count?.rental >= 1 ? 'destructive' : 'outline'}>
                      {subscription.ads_count?.rental || 0}/1
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Demandes</span>
                    <Badge variant={subscription.ads_count?.request >= 1 ? 'destructive' : 'outline'}>
                      {subscription.ads_count?.request || 0}/1
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Annuaire</span>
                    <Badge variant={subscription.ads_count?.directory >= 1 ? 'destructive' : 'outline'}>
                      {subscription.ads_count?.directory || 0}/1
                    </Badge>
                  </div>
                </div>
              ) : adUsage && (
                <>
                  <p className="text-2xl font-bold text-[#0A2540]">
                    {adUsage.used} / {adUsage.max}
                  </p>
                  <p className="text-sm text-gray-500">annonces utilisées</p>
                  <div className="mt-4">
                    <Progress value={(adUsage.used / adUsage.max) * 100} className="h-2" />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Features */}
        <Card>
          <CardHeader>
            <CardTitle>Fonctionnalités de votre pack</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <FaBox className="text-[#635BFF]" />
                <span>Voir et commander les annonces</span>
              </div>
              <div className="flex items-center gap-3">
                <FaKey className="text-[#635BFF]" />
                <span>Louer du matériel</span>
              </div>
              
              {subscription.plan_details?.features?.can_create_chantier && (
                <div className="flex items-center gap-3">
                  <FaClipboardList className="text-[#635BFF]" />
                  <span>Créer des listes de chantier</span>
                </div>
              )}
              
              {subscription.plan_details?.features?.has_supplier_shop && (
                <div className="flex items-center gap-3">
                  <FaStore className="text-[#635BFF]" />
                  <span>Boutique fournisseur</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Upgrade CTA */}
        {subscription.plan !== 'sur_mesure' && subscription.status !== 'expired' && (
          <Card className="mt-6 bg-gradient-to-r from-[#0A2540] to-[#635BFF] text-white">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h3 className="text-xl font-bold mb-2">
                    Besoin de plus de fonctionnalités ?
                  </h3>
                  <p className="text-white/80">
                    Passez à une formule supérieure pour débloquer plus d'annonces et de fonctionnalités.
                  </p>
                </div>
                <Button 
                  variant="secondary"
                  className="bg-white text-[#0A2540] hover:bg-gray-100"
                  onClick={() => navigate('/pricing')}
                >
                  Voir les offres
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
